import React, { useState } from 'react';
import DropZone from './DropZone';
import { extractDeck } from './extractDeck';
import { Deck } from './types';
import styles from './MemoBuilder.module.css';

interface Props {
  deck: Deck | null;
  onChange: (deck: Deck | null) => void;
}

type Status = { kind: 'idle' } | { kind: 'working'; done: number; total: number } | { kind: 'error'; message: string };

const DeckUpload: React.FC<Props> = ({ deck, onChange }) => {
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [openSlide, setOpenSlide] = useState<number | null>(null);

  const handleFiles = async ([file]: File[]) => {
    setStatus({ kind: 'working', done: 0, total: 0 });
    try {
      const next = await extractDeck(file, (done, total) => setStatus({ kind: 'working', done, total }));
      onChange(next);
      setOpenSlide(null);
      setStatus({ kind: 'idle' });
    } catch (err) {
      setStatus({ kind: 'error', message: err instanceof Error ? err.message : 'Could not read this file.' });
    }
  };

  const working = status.kind === 'working';
  const textSlides = deck ? deck.slides.filter((s) => s.text).length : 0;
  const active = deck?.slides.find((s) => s.number === openSlide);

  return (
    <div className={styles.stack}>
      {!deck && (
        <DropZone
          accept=".pdf,.pptx,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation"
          disabled={working}
          title={working ? 'Reading deck...' : 'Drop a pitch deck or click to browse'}
          hint="PDF or PPTX"
          onFiles={handleFiles}
        />
      )}

      {working && (
        <div className={styles.progress}>
          <div
            className={styles.progressBar}
            style={{ width: status.total ? `${(status.done / status.total) * 100}%` : '4%' }}
          />
          <span className={styles.progressLabel}>
            {status.total ? `Processing slide ${status.done} of ${status.total}` : 'Opening file...'}
          </span>
        </div>
      )}

      {status.kind === 'error' && <p className={styles.error}>{status.message}</p>}

      {deck && (
        <>
          <div className={styles.fileRow}>
            <span className={styles.fileBadge}>{deck.kind.toUpperCase()}</span>
            <div className={styles.fileMeta}>
              <span className={styles.fileName}>{deck.fileName}</span>
              <span className={styles.muted}>
                {deck.slides.length} slides, {textSlides} with text
                {deck.kind === 'pdf' && ', all rendered as images'}
              </span>
            </div>
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => {
                onChange(null);
                setStatus({ kind: 'idle' });
              }}
            >
              Replace
            </button>
          </div>

          {textSlides < deck.slides.length && deck.kind === 'pdf' && (
            <p className={styles.note}>
              Some pages have no text layer (likely image-only). Their rendered images are kept so charts and metrics
              can still be read.
            </p>
          )}

          <div className={styles.slideGrid}>
            {deck.slides.map((slide) => (
              <button
                type="button"
                key={slide.number}
                className={`${styles.slideThumb} ${openSlide === slide.number ? styles.slideThumbActive : ''}`}
                onClick={() => setOpenSlide(openSlide === slide.number ? null : slide.number)}
                title={`Slide ${slide.number}`}
              >
                {slide.imageDataUrl ? (
                  <img src={slide.imageDataUrl} alt={`Slide ${slide.number}`} />
                ) : (
                  <span className={styles.slideThumbText}>{slide.text.split('\n')[0] || 'No text'}</span>
                )}
                <span className={styles.slideNumber}>{slide.number}</span>
              </button>
            ))}
          </div>

          {active && (
            <div className={styles.slideDetail}>
              <div className={styles.slideDetailHeader}>
                <strong>Slide {active.number}</strong>
                <button type="button" className={styles.linkButton} onClick={() => setOpenSlide(null)}>
                  Close
                </button>
              </div>
              {active.imageDataUrl && <img src={active.imageDataUrl} alt={`Slide ${active.number}`} />}
              <pre className={styles.extracted}>{active.text || 'No extractable text on this slide.'}</pre>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DeckUpload;
